import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserStateMap, UserStateEntry } from '../../../types/user-state';
import { isSavedJobKey, isViewedJobKey, isAppliedJobKey } from '../../../types/user-state';

// Mutually exclusive states: only one can be active at a time
export type ApplicationState = 'interviewing' | 'offer' | 'rejected' | 'hired' | null;

// Independent flags: can coexist with any mutually exclusive state
export interface Job {
  id: string;
  applicationState?: ApplicationState; // Mutually exclusive: interviewing, offer, rejected, hired, or null
  isHidden?: boolean; // Independent flag: can be true alongside any applicationState
  /** Pasted off-site application link (stored in user state); not an Algolia record */
  externalUrl?: string;
  /** Employer display name when known (e.g. from Algolia hit) */
  companyName?: string;
  [key: string]: unknown;
}

export interface JobsState {
  viewedJobs: Job[];
  savedJobs: Job[];
  appliedJobs: Job[];
}

const initialState: JobsState = {
  viewedJobs: [],
  savedJobs: [],
  appliedJobs: [],
};

/**
 * Extract jobs from userState for SSR hydration
 */
export function extractJobsFromUserState(userState: UserStateMap): JobsState {
  const viewedJobs: Job[] = [];
  const savedJobs: Job[] = [];
  const appliedJobs: Job[] = [];

  Object.entries(userState).forEach(([itemKey, entry]) => {
    const entryData = entry.data as Record<string, unknown>;
    const jobId = entryData.id ?? entryData.objectID;
    
    if (!jobId) {
      return;
    }

    const job: Job = {
      id: String(jobId),
      title: entryData.title,
      normalisedTitle: entryData.normalisedTitle,
      companyId: entryData.companyId,
      companyName: typeof entryData.companyName === 'string' ? entryData.companyName : undefined,
    } as Job;

    // Extract application state from appliedJob# entry
    // applicationState is stored as a field in the appliedJob# entry (mutually exclusive, only applies to applied jobs)
    if (
      entryData.applicationState !== undefined &&
      ['interviewing', 'offer', 'rejected', 'hired', null].includes(entryData.applicationState as ApplicationState)
    ) {
      job.applicationState = entryData.applicationState as ApplicationState;
    }

    // Extract hidden flag
    if (entryData.isHidden === true) {
      job.isHidden = true;
    }

    // Extract timestamps
    if (entryData.viewedAt) {
      (job as Record<string, unknown>).viewedAt = entryData.viewedAt;
    }
    if (entryData.appliedAt) {
      (job as Record<string, unknown>).appliedAt = entryData.appliedAt;
    }
    if (entryData.savedAt) {
      (job as Record<string, unknown>).savedAt = entryData.savedAt;
    }
    if (typeof entryData.externalUrl === 'string' && entryData.externalUrl.length > 0) {
      job.externalUrl = entryData.externalUrl;
    }

    // Add to appropriate array based on itemKey
    if (isSavedJobKey(itemKey)) {
      savedJobs.push(job);
    }
    if (isViewedJobKey(itemKey)) {
      viewedJobs.push(job);
    }
    if (isAppliedJobKey(itemKey)) {
      appliedJobs.push(job);
    }
  });

  // Log summary only if there's a significant discrepancy
  const isServer = typeof window === 'undefined';
  const totalJobEntries = savedJobs.length + viewedJobs.length + appliedJobs.length;
  const totalUserStateEntries = Object.keys(userState).filter(k => 
    k.startsWith('savedJob#') || k.startsWith('viewedJob#') || k.startsWith('appliedJob#')
  ).length;
  
  if (isServer && totalJobEntries !== totalUserStateEntries) {
    console.warn('[extractJobsFromUserState] SSR: Job extraction mismatch:', {
      totalUserStateEntries,
      extractedJobs: totalJobEntries,
      savedCount: savedJobs.length,
      viewedCount: viewedJobs.length,
      appliedCount: appliedJobs.length,
    });
  }

  return {
    viewedJobs,
    savedJobs,
    appliedJobs,
  };
}

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    addViewedJob: (state, action: PayloadAction<Job>) => {
      if (!state.viewedJobs.some((job) => job.id === action.payload.id)) {
        state.viewedJobs.push(action.payload);
      }
    },
    removeViewedJob: (state, action: PayloadAction<string>) => {
      state.viewedJobs = state.viewedJobs.filter((job) => job.id !== action.payload);
    },
    addSavedJob: (state, action: PayloadAction<Job>) => {
      if (!state.savedJobs.some((job) => job.id === action.payload.id)) {
        state.savedJobs.push(action.payload);
      }
    },
    removeSavedJob: (state, action: PayloadAction<string>) => {
      state.savedJobs = state.savedJobs.filter((job) => job.id !== action.payload);
    },
    addAppliedJob: (state, action: PayloadAction<Job>) => {
      if (!state.appliedJobs.some((job) => job.id === action.payload.id)) {
        state.appliedJobs.push(action.payload);
      }
    },
    removeAppliedJob: (state, action: PayloadAction<string>) => {
      state.appliedJobs = state.appliedJobs.filter((job) => job.id !== action.payload);
    },
    updateJobApplicationState: (state, action: PayloadAction<{ jobId: string; applicationState: ApplicationState }>) => {
      const { jobId, applicationState } = action.payload;
      
      // Update in all job arrays - mutually exclusive states replace each other
      const updateJobInArray = (jobs: Job[]) => {
        const job = jobs.find((j) => j.id === jobId);
        if (job) {
          job.applicationState = applicationState;
        }
      };
      
      updateJobInArray(state.viewedJobs);
      updateJobInArray(state.savedJobs);
      updateJobInArray(state.appliedJobs);
    },
    updateJobHiddenState: (state, action: PayloadAction<{ jobId: string; isHidden: boolean }>) => {
      const { jobId, isHidden } = action.payload;
      
      // Update hidden flag independently in all job arrays
      const updateJobInArray = (jobs: Job[]) => {
        const job = jobs.find((j) => j.id === jobId);
        if (job) {
          job.isHidden = isHidden;
        }
      };
      
      updateJobInArray(state.viewedJobs);
      updateJobInArray(state.savedJobs);
      updateJobInArray(state.appliedJobs);
    },
    hydrateJobsFromSSR: (state, action: PayloadAction<UserStateMap>) => {
      const extractedJobs = extractJobsFromUserState(action.payload);
      // Merge with existing jobs, avoiding duplicates
      const mergeJobs = (existing: Job[], incoming: Job[]) => {
        const existingIds = new Set(existing.map(j => j.id));
        const newJobs = incoming.filter(j => !existingIds.has(j.id));
        return [...existing, ...newJobs];
      };
      state.viewedJobs = mergeJobs(state.viewedJobs, extractedJobs.viewedJobs);
      state.savedJobs = mergeJobs(state.savedJobs, extractedJobs.savedJobs);
      state.appliedJobs = mergeJobs(state.appliedJobs, extractedJobs.appliedJobs);
    },
  },
});

export const { addViewedJob, removeViewedJob, addSavedJob, removeSavedJob, addAppliedJob, removeAppliedJob, updateJobApplicationState, updateJobHiddenState, hydrateJobsFromSSR } = jobsSlice.actions;
export default jobsSlice.reducer;
